import type { TFunction } from 'i18next'
import {
  SHIPPING_OPTION_EXPRESS,
  SHIPPING_OPTION_FREE,
  SHIPPING_OPTION_PRIORITY,
} from '../../stores/shippingStore/shipping.consts'
import type { ShippingOption } from '../../stores/shippingStore/shipping.types'
import { getDeliveryDateDescription } from '../../stores/shippingStore/shipping.utils'
import { getStripeAmountToCharge } from '../../utils/utils'
import type { ExpressCheckoutRate } from '../Checkout/expressCheckoutPricing'
import type { ExpressCheckoutDeliveryOptions } from '../Checkout/useExpressCheckoutShippingOptionsFetcher'

const SHIPPING_LABEL_KEYS: Record<string, string> = {
  [SHIPPING_OPTION_FREE]: 'order.checkout.shipping_options.standard',
  [SHIPPING_OPTION_EXPRESS]: 'order.checkout.shipping_options.express_name',
  [SHIPPING_OPTION_PRIORITY]: 'order.checkout.shipping_options.priority_name',
}

export const getShippingRateLabel = (t: TFunction, id: string) =>
  t(SHIPPING_LABEL_KEYS[id] ?? SHIPPING_LABEL_KEYS[SHIPPING_OPTION_FREE])

export const toStripeShippingRate = (
  t: TFunction,
  { id, summary }: ExpressCheckoutRate,
  shippingOptions: ShippingOption[]
) => {
  const shippingOption = shippingOptions.find(
    (option) => option.shippingOptionName === id
  )
  const deliveryEstimate = shippingOption
    ? getDeliveryDateDescription(t, shippingOption, false)
    : undefined
  return {
    id,
    amount: getStripeAmountToCharge({
      totalCost: summary.shippingCost ?? 0,
      currency: summary.currency,
    }),
    displayName: getShippingRateLabel(t, id),
    ...(deliveryEstimate ? { deliveryEstimate } : {}),
  }
}

export function getOfferedRates(
  rates: ExpressCheckoutRate[],
  delivery: ExpressCheckoutDeliveryOptions
): ExpressCheckoutRate[] {
  // A failed lookup vouches for nothing, so only standard shipping is offered.
  // An empty one means nothing was quoted, so there are no options to filter by.
  if (delivery.hasFailed) {
    return rates.filter((rate) => rate.id === SHIPPING_OPTION_FREE)
  }
  if (delivery.options.length === 0) {
    return rates
  }
  return rates.filter((rate) =>
    delivery.options.some((option) => option.shippingOptionName === rate.id)
  )
}
